<template>
  <div class="container" :style="`--primary-500: ${primaryValue}; --primary-DEFAULT: ${primaryValue};`">
    <div class="tw-flex tw-justify-center">
      <base-card shadow class="tw-mt-4 tw-w-full sm:tw-max-w-md">
        <div class="tw-text-center tw-text-gray-800 tw-text-lg tw-font-light tw-mt-3">Activate Plans</div>
        <div class="tw-text-left tw-text-gray-800 tw-text-sm tw-mt-3 tw-font-light">
          {{ businessName }} uses the Predictile platform to enable easy and secure savings plans. Add this plan to your Predictile Savings account and enhance your savings today!
        </div>
        <div class="tw-mt-4">Card</div>
        <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :style="`--primary-50: ${lightPrimaryValue}`" :class="{ 'tw-bg-primary-50': paymentOption === 'cardOnFile' }">
          <base-radio class="tw-mr-2" value="cardOnFile" name="paymentOption" v-model="paymentOption"></base-radio>
          <div class="tw-flex tw-justify-between tw-w-full">
            <div>
              <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Card on file</div>
              <div class="tw-text-gray-500 tw-text-sm">Uses current default card on file.</div>
            </div>
            <div class="tw-flex tw-text-right">
              <credit-card-icon class="tw-w-8 tw-h-8 tw-my-auto" :name="savedCreditCard.brand" />
              <div class="tw-ml-3 tw-text-sm" v-if="savedCreditCard.last4">
                <div>**** {{ savedCreditCard.last4 }}</div>
                <div class="tw-text-gray-500 tw-text-xs">Exp {{ expDate(savedCreditCard.expMonth, savedCreditCard.expYear) }}</div>
              </div>
              <div class="tw-ml-3 tw-text-gray-500" v-else>
                No Card
              </div>
            </div>
          </div>
        </base-card>
        <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :style="`--primary-50: ${lightPrimaryValue}`" :class="{ 'tw-bg-primary-50': paymentOption === 'manual' }">
          <base-radio class="tw-mr-2" value="manual" name="paymentOption" v-model="paymentOption"></base-radio>
          <div class="tw-flex tw-flex-col tw-w-full">
            <div class="tw-text-gray-800 tw-text-sm tw-font-normal">New card (replaces existing)</div>
            <base-credit-card-input class="tw-mt-3" ref="creditCardInput" hide-name hide-postal-code />
          </div>
        </base-card>

        <div class="tw-text-gray-800 tw-mt-4">Plan Details:</div>
        <base-list-group class="tw-mt-2">
          <base-list-item v-for="plan in selectedPlans" :key="plan.businessPlanId">
            <div class="tw-flex tw-justify-between">
              <div class="tw-text-gray-800 tw-text-sm">{{ plan.name }}</div>
              <base-button type="text" @click="planTermsModal(plan.businessPlanId)">Terms</base-button>
            </div>
          </base-list-item>
        </base-list-group>

        <base-form class="tw-my-4" ref="form" v-model="isFormValid">
          <base-checkbox-group required v-model="termsAcceptance">
            <base-checkbox
              label="I agree to the plan fees plus applicable taxes and service fees for the plans detailed above for the duration of the plan terms."
              value="isAccepted"
              v-model="termsAcceptance"
              required
            />
          </base-checkbox-group>
        </base-form>
        <inline-error id="signup-payment-card-error" class="tw-my-3" :title="errorTitle" :message="errorMessage" :error="error" />
        <div class="tw-flex tw-w-full">
          <base-button
            class="tw-w-full"
            :style="`--primary-500: ${secondaryValue}; --primary-600: ${secondaryValue};`"
            type="primary"
            @click="submit()"
            :loading="isLoading"
            :disabled="isLoading"
          >
            <span> Let's get started! </span>
          </base-button>
        </div>
      </base-card>
    </div>
    <base-modal ref="planTermsModal">
      <base-card>
        <div class="tw-flex tw-justify-between tw-mb-6">
          <div class="tw-text-xl tw-font-thin tw-my-auto">{{ termsBusinessPlan.name }} Plan Terms and Conditions</div>
          <base-icon class="tw-my-auto"><x-icon class="tw-w-5 tw-h-5" @click="planTermsModalHide"/></base-icon>
        </div>
        <business-plan-agreement
          :businessPlanId="termsBusinessPlan.businessPlanId"
          :priceCents="termsBusinessPlan.priceCents"
          :currency="termsBusinessPlan.priceCurrency"
          :downPaymentCents="termsBusinessPlan.downPaymentCents"
          :businessName="businessName"
          :termLength="termsBusinessPlan.termLength"
          :termInterval="termsBusinessPlan.termInterval"
          :cycleFrequency="termsBusinessPlan.cycleFrequency"
          :cycleInterval="termsBusinessPlan.cycleInterval"
          :serviceFeeCents="termsBusinessPlan.serviceFeeCents"
        />
        <div class="tw-flex tw-w-full tw-justify-end">
          <base-button @click="planTermsModalHide" type="secondary" color="gray" class=" sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
            Close
          </base-button>
        </div>
      </base-card>
    </base-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BusinessPlanAgreement from "@/views/business/business-plans/components/BusinessPlanAgreement.vue";
import { padStart } from "lodash";
import CreditCardIcon from "@/components/CreditCardIcon.vue";
import chroma from "chroma-js";
import InlineError from "@/components/InlineError";
import { XIcon } from "@/assets/svgs";

export default {
  components: {
    BusinessPlanAgreement,
    CreditCardIcon,
    InlineError,
    XIcon,
  },
  props: {
    businessId: String,
    businessName: String,
    primaryColor: String,
    secondaryColor: String,
    operatingModel: String,
    location: Object,
    selectedPlans: Array,
    collectAddressSetting: Boolean,
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      errorTitle: "",
      errorMessage: "",
      error: null,
      termsBusinessPlan: {},
      termsAcceptance: [],
      paymentOption: "cardOnFile",
      savedCreditCard: { brand: "", last4: null, expMonth: 1, expYear: 2023 },
      token: null,
    };
  },
  computed: {
    ...mapGetters("cognito", { userId: "id" }),
    primaryValue() {
      return this.primaryColor ? this.primaryColor : "#ff0090";
    },
    lightPrimaryValue() {
      return chroma(this.primaryColor ? this.primaryColor : "#ff0090")
        .brighten(1.1)
        .alpha(0.3);
    },
    secondaryValue() {
      return this.secondaryColor ? this.secondaryColor : "#404040";
    },
  },
  created() {
    this.getCardOnFile();
  },
  mounted() {
    this.termsBusinessPlan = this.selectedPlans[0];
  },
  methods: {
    expDate(expMonth, expYear) {
      if (expMonth && expYear) return `${padStart(expMonth, 2, "0")} / ${expYear.toString().slice(-2)}`;
      else return "unknown";
    },
    planTermsModal(businessPlanId) {
      this.termsBusinessPlan = this.selectedPlans[this.selectedPlans.findIndex((x) => x.businessPlanId === businessPlanId)];
      this.$refs.planTermsModal.show();
    },
    planTermsModalHide() {
      this.$refs.planTermsModal.hide();
    },
    async submit() {
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;

      if (this.paymentOption === "cardOnFile" && this.savedCreditCard.last4 === null) {
        this.errorTitle = "No payment method chosen";
        this.errorMessage = "You must enter a valid credit card.";
        this.error = null;
        return;
      }

      if (!this.$refs.form.validate()) return;

      if (this.paymentOption === "manual") {
        this.token = await this.$refs.creditCardInput.createToken();
        if (!this.token) return;
      }

      this.isLoading = true;

      this.$axios
        .post(`/businesses/${this.businessId}/locations/${this.location.businessLocationId}/member-web-signup/payment`, {
          businessPlanIds: this.selectedPlans.map((x) => x["businessPlanId"]),
          userId: this.userId,
          tokenId: this.token ? this.token.id : null,
        })
        .then(() => {
          this.isLoading = false;
          this.$router.push(`/businesses/${this.businessId}/member-web-signup/success`);
        })
        .catch((err) => {
          this.isLoading = false;
          this.errorTitle = `Failed to start plan`;
          this.errorMessage = `We were not able to start the plan. Please check the error message below and try again.`;
          this.error = err;
        });
    },
    getCardOnFile() {
      const url = `/businesses/${this.businessId}/members/${this.userId}/platformcard`;
      this.$axios
        .get(url)
        .then((res) => {
          if (res.data.last4) {
            this.savedCreditCard = res.data;
            this.paymentOption = "cardOnFile";
          } else {
            this.paymentOption = "manual";
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
